/**
 * Áreas del árbol organizacional (`/api/areas`).
 *
 * ⚠️ Nombre LEGACY: la entidad se llama `Area` y las rutas son `/api/areas`. Se
 * conserva la PK `id_programa` porque un programa es la hoja canónica del árbol.
 *
 * Árbol sede → facultad → programa SIN límite de profundidad, a diferencia del
 * árbol de componentes. Es un bosque: admite varias raíces, una por sede.
 *
 * Capa de servicios: una función por operación del API. No lanza nunca —cada
 * función devuelve un valor neutro ante el fallo—, así que el llamador debe
 * comprobar el resultado, no envolver en try/catch.
 */

import type { Programa } from '@/interfaces/Programa'
import { http } from '@/services/http'

const BASE = '/api/areas'

/**
 * Consulta programa · petición `GET`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `[]`. Un array
 * vacío NO distingue «sin datos» de «la petición falló».
 *
 * @returns Programa[]
 */
export async function getProgramasService(idCampus?: number | null): Promise<Programa[]> {
  const url = idCampus ? `${BASE}?id_campus=${idCampus}` : BASE
  const result = await http.get<any>(url)
  if (!result.ok) return []
  const d = result.data
  return Array.isArray(d) ? d : []
}

/**
 * Consulta programa · petición `GET`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `[]`. Un array
 * vacío NO distingue «sin datos» de «la petición falló».
 *
 * @returns Programa[]
 */
export async function getArbolAreasService(idCampus?: number | null): Promise<Programa[]> {
  const url = idCampus ? `${BASE}/arbol?id_campus=${idCampus}` : `${BASE}/arbol`
  const result = await http.get<any>(url)
  if (!result.ok) return []
  const d = result.data
  return Array.isArray(d) ? d : []
}

/**
 * Consulta programa · petición `GET`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `null`. Un null
 * NO distingue «no encontrado» de «la petición falló».
 *
 * @returns Programa | null
 */
export async function getProgramaByIdService(id: number): Promise<Programa | null> {
  const result = await http.get<Programa>(`${BASE}/${id}`)
  return result.ok ? (result.data ?? null) : null
}

/**
 * Asigna programa · petición `POST`.
 *
 * @returns { ok: boolean; data?: Programa; errors?: Record<string, string[]> }
 */
export async function asignarCampusAreaService(
  idArea: number,
  idCampus: number
): Promise<{ ok: boolean; data?: Programa; errors?: Record<string, string[]> }> {
  const result = await http.post<Programa>(`${BASE}/${idArea}/campus`, { id_campus: idCampus })
  if (!result.ok) return { ok: false, errors: result.errors }
  return { ok: true, data: result.data }
}

/**
 * Elimina programa · petición `DELETE`.
 *
 * @returns { ok: boolean; errors?: Record<string, string[]> }
 */
export async function desasignarCampusAreaService(
  idArea: number,
  idCampus: number
): Promise<{ ok: boolean; errors?: Record<string, string[]> }> {
  const result = await http.delete(`${BASE}/${idArea}/campus/${idCampus}`)
  if (!result.ok) return { ok: false, errors: result.errors }
  return { ok: true }
}

/**
 * Crea programa · petición `POST`.
 *
 * @returns { ok: boolean; data?: Programa; errors?: Record<string, string[]> }
 */
export async function crearProgramaService(
  payload: Omit<Programa, 'id_programa' | 'ordinal' | 'hijos_recursivos' | 'campus'>
): Promise<{ ok: boolean; data?: Programa; errors?: Record<string, string[]> }> {
  const clean = Object.fromEntries(Object.entries(payload).filter(([, v]) => v !== undefined))
  const result = await http.post<Programa>(BASE, clean)
  if (!result.ok) return { ok: false, errors: result.errors }
  return { ok: true, data: result.data }
}

/**
 * Actualiza programa · petición `PUT`.
 *
 * @returns { ok: boolean; data?: Programa; errors?: Record<string, string[]> }
 */
export async function updateProgramaService(
  id: number,
  payload: Partial<Programa>
): Promise<{ ok: boolean; data?: Programa; errors?: Record<string, string[]> }> {
  const clean = Object.fromEntries(Object.entries(payload).filter(([, v]) => v !== undefined))
  const result = await http.put<Programa>(`${BASE}/${id}`, clean)
  if (!result.ok) return { ok: false, errors: result.errors }
  return { ok: true, data: result.data }
}

/**
 * Reordena programa · petición `PUT`.
 *
 * @returns { ok: boolean; data?: any; message?: string }
 */
export async function reordenarAreaService(
  id: number,
  posicion: number
): Promise<{ ok: boolean; data?: any; message?: string }> {
  const result = await http.put<any>(`${BASE}/${id}/reordenar`, { posicion })
  if (!result.ok) {
    const msg = Object.values(result.errors ?? {}).flat()[0] ?? `HTTP ${result.status}`
    return { ok: false, message: msg }
  }
  return { ok: true, data: result.data }
}

/**
 * Elimina programa · petición `DELETE`.
 *
 * @returns { ok: boolean; errors?: Record<string, string[]> }
 */
export async function eliminarProgramaService(
  id: number
): Promise<{ ok: boolean; errors?: Record<string, string[]> }> {
  const result = await http.delete(`${BASE}/${id}`)
  if (!result.ok) return { ok: false, errors: result.errors }
  return { ok: true }
}
